import type * as React from "react"

import type { SearchResult, SearchResultKind } from "@ibada/shared-types/search"

/** How global search was opened (analytics `open_mode`). */
export enum SearchOpenMode {
  Shortcut = "shortcut",
  Click = "click",
  /** The search field on the phone home, which opens the phone search screen. */
  PhoneHome = "phone_home",
}

/**
 * Router state asking the desktop header to open the palette on arrival, with the query already typed. The
 * search route sends it when opened on a desktop (a shared phone link), since a desktop searches in the palette.
 */
export type GlobalSearchOpenRequest = { globalSearch: { query: string } }

export function readGlobalSearchOpenRequest(state: unknown): GlobalSearchOpenRequest["globalSearch"] | null {
  if (!state || typeof state !== "object" || !("globalSearch" in state)) return null
  const request = (state as GlobalSearchOpenRequest).globalSearch
  return request && typeof request.query === "string" ? request : null
}

/** Palette entries that do not come from the search endpoint. */
export enum SearchNavKind {
  Page = "page",
  Action = "action",
}

/** What a palette entry points at: a server result kind, an app page or a quick action. */
export type SearchEntryKind = SearchResultKind | SearchNavKind

/** The palette's groups, in display order. */
export enum SearchPaletteGroupKey {
  Recents = "recents",
  QuickActions = "quickActions",
  Members = "members",
  Finance = "finance",
  Activities = "activities",
  Groups = "groups",
  Ministries = "ministries",
  Departments = "departments",
  Forms = "forms",
  Assets = "assets",
  Pages = "pages",
  Actions = "actions",
}

/** A recently opened entry: only what is needed to show and reopen it, never record contents. */
export type SearchRecentEntry = {
  id: string
  kind: SearchEntryKind
  title: string
  url: string
}

/** An app page or quick action offered by the palette (already translated and permission-filtered). */
export type SearchNavEntry = {
  id: string
  kind: SearchNavKind
  title: string
  subtitle: string | null
  url: string
  icon: React.ReactNode
  /** Synonyms matched alongside the title (never displayed). */
  keywords: readonly string[]
}

/** One selectable row of the palette. `value` is unique across the palette and drives the preview. */
export type SearchPaletteEntry = {
  value: string
  id: string
  kind: SearchEntryKind
  title: string
  subtitle: string | null
  url: string
  /** The server result behind the row, when there is one (the preview reads its facts). */
  result: SearchResult | null
  /** Icon for page / action / recent rows. */
  icon: React.ReactNode | null
  /** True for a row coming from the recents list. */
  recent: boolean
}

export type SearchPaletteGroup = {
  key: SearchPaletteGroupKey
  entries: SearchPaletteEntry[]
  /** Matches found server side for this group (may exceed `entries.length`). */
  total: number | null
}
